import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Briefcase, Factory, AlertTriangle } from "lucide-react";

const ATTENTION_COLS = ["Issue", "Owner", "Department", "Deadline", "Delay", "Status", "Required Action"];

function Stat({ label, value, tone = "" }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`metric text-2xl font-extrabold mt-1 ${tone}`}>{value}</p>
    </div>
  );
}

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/command-center");
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 text-muted-foreground" data-testid="command-center">Loading…</div>;
  const { office, operations, attention } = data;

  return (
    <div className="space-y-6" data-testid="command-center">
      <SectionTitle>WIPPLOPS Command Center</SectionTitle>

      <div data-testid="cc-office">
        <h3 className="flex items-center gap-2 text-lg font-bold uppercase tracking-tight mb-3">
          <Briefcase className="w-5 h-5 text-primary" /> Office Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Active Tasks" value={office.active} />
          <Stat label="Completed Today" value={office.completed_today} tone="text-signal-green" />
          <Stat label="Overdue" value={office.overdue} tone={office.overdue ? "text-signal-red" : ""} />
          <Stat label="Blocked" value={office.blocked} tone={office.blocked ? "text-signal-amber" : ""} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <p className="px-4 pt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Department Performance</p>
            <table className="w-full text-sm mt-1" data-testid="cc-dept-table">
              <thead><tr className="border-b border-border text-left">
                {["Department", "Open", "Overdue", "Done (7d)"].map((h) => (
                  <th key={h} className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
              </tr></thead>
              <tbody>
                {office.department_performance.map((d) => (
                  <tr key={d.department} className="border-b border-border/50" data-testid={`cc-dept-${d.department.replace(/\s+/g, "-")}`}>
                    <td className="px-4 py-2">{d.department}</td>
                    <td className="px-4 py-2 metric">{d.open}</td>
                    <td className={`px-4 py-2 metric font-bold ${d.overdue ? "text-signal-red" : ""}`}>{d.overdue}</td>
                    <td className="px-4 py-2 metric text-signal-green">{d.done_week}</td>
                  </tr>))}
                {office.department_performance.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No office tasks yet</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <p className="px-4 pt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Employee Workload (open)</p>
            <table className="w-full text-sm mt-1" data-testid="cc-workload-table">
              <thead><tr className="border-b border-border text-left">
                {["Employee", "Open", "Overdue"].map((h) => (
                  <th key={h} className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
              </tr></thead>
              <tbody>
                {office.workload.map((w) => (
                  <tr key={w.name} className="border-b border-border/50">
                    <td className="px-4 py-2">{w.name}</td>
                    <td className="px-4 py-2 metric">{w.open}</td>
                    <td className={`px-4 py-2 metric font-bold ${w.overdue ? "text-signal-red" : ""}`}>{w.overdue}</td>
                  </tr>))}
                {office.workload.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div data-testid="cc-operations">
        <h3 className="flex items-center gap-2 text-lg font-bold uppercase tracking-tight mb-3">
          <Factory className="w-5 h-5 text-signal-orange" /> Operations Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Orders In Progress" value={operations.counts?.in_progress ?? "—"} />
          <Stat label="Production Pending" value={operations.production_pending} tone={operations.production_pending ? "text-signal-amber" : ""} />
          <Stat label="Delayed Orders" value={operations.delayed_orders} tone={operations.delayed_orders ? "text-signal-red" : ""} />
          <Stat label="Open Exceptions" value={operations.exceptions_open} tone={operations.exceptions_open ? "text-signal-red" : ""} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
          {Object.entries(operations.kpis || {}).map(([k, v]) => (
            <div key={k} className="bg-card border border-border rounded-lg p-3">
              <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</p>
              <p className="metric text-lg font-bold mt-0.5">{v != null ? v : "—"}</p>
            </div>))}
        </div>
      </div>

      <div data-testid="cc-attention">
        <h3 className="flex items-center gap-2 text-lg font-bold uppercase tracking-tight mb-3 text-signal-red">
          <AlertTriangle className="w-5 h-5" /> Management Attention Required ({attention.length})</h3>
        <div className="bg-card border border-signal-red/30 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="cc-attention-table">
            <thead><tr className="border-b border-border text-left">
              {ATTENTION_COLS.map((h) => (
                <th key={h} className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
            </tr></thead>
            <tbody>
              {attention.map((a, i) => (
                <tr key={`${a.ref}-${i}`} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" onClick={() => a.link && navigate(a.link)}
                  data-testid={`attention-row-${i}`}>
                  <td className="px-3 py-2.5 text-xs font-medium">{a.issue}</td>
                  <td className="px-3 py-2.5 text-xs">{a.owner}</td>
                  <td className="px-3 py-2.5 text-xs">{a.department}</td>
                  <td className="px-3 py-2.5 metric text-xs">{a.deadline || "—"}</td>
                  <td className="px-3 py-2.5 metric text-xs text-signal-red">{a.delay}</td>
                  <td className="px-3 py-2.5 text-xs uppercase">{a.status}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{a.required_action}</td>
                </tr>))}
              {attention.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-signal-green">All clear — nothing needs management attention</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
